import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaPlus, FaFileContract, FaBan, FaSync } from 'react-icons/fa';
import Swal from 'sweetalert2';
import '../../styles/pages/RRHH/ContratosPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Página de Contratos Laborales
 * Gestión de contratos de trabajo
 */
const ContratosPage = ({ token, id_company }) => {
    const [contratos, setContratos] = useState([]);
    const [colaboradores, setColaboradores] = useState([]); // Todos (para filtros)
    const [colaboradoresDisponibles, setColaboradoresDisponibles] = useState([]); // Sin finiquito pagado
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);

    // Filtros
    const [filtroEstado, setFiltroEstado] = useState('VIGENTE');
    const [filtroColaborador, setFiltroColaborador] = useState('');

    // Formulario
    const [formData, setFormData] = useState({
        id_colaborador: '',
        tipo_contrato: 'INDEFINIDO',
        fecha_inicio: '',
        fecha_termino: '',
        sueldo_base: '',
        jornada: 'COMPLETA',
        horas_semanales: 45,
        cargo: '',
        lugar_trabajo: '',
        observaciones: ''
    });

    const fetchColaboradores = useCallback(async () => {
        try {
            // Cargar todos los colaboradores (para filtros)
            const responseAll = await axios.get(`${API_BASE_URL}/api/rrhh/colaboradores`, {
                params: { id_company },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (responseAll.data.success) {
                setColaboradores(responseAll.data.data);
            }

            // Cargar colaboradores disponibles (sin finiquito pagado)
            const responseDisponibles = await axios.get(`${API_BASE_URL}/api/rrhh/colaboradores`, {
                params: { id_company, excluir_finiquitados: 'true' },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (responseDisponibles.data.success) {
                setColaboradoresDisponibles(responseDisponibles.data.data);
            }
        } catch (error) {
            console.error('Error al cargar colaboradores:', error);
        }
    }, [id_company, token]);

    const fetchContratos = useCallback(async () => {
        try {
            setLoading(true);
            const params = { id_company, estado: filtroEstado };
            if (filtroColaborador) params.id_colaborador = filtroColaborador;

            const response = await axios.get(`${API_BASE_URL}/api/rrhh/contratos`, {
                params,
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setContratos(response.data.data);
            }
        } catch (error) {
            console.error('Error al cargar contratos:', error);
            Swal.fire('Error', 'No se pudieron cargar los contratos', 'error');
        } finally {
            setLoading(false);
        }
    }, [id_company, token, filtroEstado, filtroColaborador]);

    useEffect(() => {
        fetchColaboradores();
    }, [fetchColaboradores]);

    useEffect(() => {
        fetchContratos();
    }, [fetchContratos]);

    const handleReemplazarPdf = async (contrato) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pdf';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            try {
                const formData = new FormData();
                formData.append('pdf', file);
                formData.append('id_company', id_company);
                formData.append('id_contrato', contrato.id);
                formData.append('id_colaborador', contrato.id_colaborador);

                await axios.post(
                    `${API_BASE_URL}/api/rrhh/contratos/upload-pdf`,
                    formData,
                    { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
                );

                Swal.fire('PDF reemplazado', 'El PDF del contrato fue actualizado en R2', 'success');
                fetchContratos();
            } catch (error) {
                console.error('Error al reemplazar PDF:', error);
                Swal.fire('Error', 'No se pudo reemplazar el PDF', 'error');
            }
        };
        input.click();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const response = await axios.post(`${API_BASE_URL}/api/rrhh/contratos`, {
                ...formData,
                id_company
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                if (response.data.pdfUrl) {
                    Swal.fire({
                        title: 'Contrato Generado',
                        text: 'El contrato ha sido generado exitosamente',
                        icon: 'success',
                        showCancelButton: true,
                        confirmButtonText: 'Descargar PDF',
                        cancelButtonText: 'Cerrar'
                    }).then((result) => {
                        if (result.isConfirmed) {
                            window.open(response.data.pdfUrl.startsWith('http') ? response.data.pdfUrl : `${API_BASE_URL}${response.data.pdfUrl}`, '_blank');
                        }
                    });
                } else {
                    Swal.fire('Éxito', 'Contrato generado correctamente', 'success');
                }

                setShowModal(false);
                resetForm();
                fetchContratos();
            }
        } catch (error) {
            console.error('Error al guardar contrato:', error);
            Swal.fire('Error', error.response?.data?.message || 'Error al guardar', 'error');
        }
    };

    const handleTerminar = async (id) => {
        try {
            const { value: formValues } = await Swal.fire({
                title: 'Terminar Contrato',
                html:
                    '<input id="fecha_termino" type="date" class="swal2-input" placeholder="Fecha de término">' +
                    '<textarea id="motivo" class="swal2-input" placeholder="Motivo de término"></textarea>',
                focusConfirm: false,
                showCancelButton: true,
                confirmButtonText: 'Terminar',
                cancelButtonText: 'Cancelar',
                preConfirm: () => {
                    return {
                        fecha_termino_real: document.getElementById('fecha_termino').value,
                        motivo_termino: document.getElementById('motivo').value
                    };
                }
            });

            if (formValues && formValues.fecha_termino_real) {
                const response = await axios.patch(
                    `${API_BASE_URL}/api/rrhh/contratos/${id}/terminar`,
                    formValues,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (response.data.success) {
                    Swal.fire('Éxito', 'Contrato terminado correctamente', 'success');
                    fetchContratos();
                }
            }
        } catch (error) {
            console.error('Error al terminar contrato:', error);
            Swal.fire('Error', 'No se pudo terminar el contrato', 'error');
        }
    };

    const handleColaboradorChange = async (id_colaborador) => {
        setFormData({...formData, id_colaborador});

        if (id_colaborador) {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/rrhh/colaboradores/${id_colaborador}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.success) {
                    const colaborador = response.data.data;
                    setFormData(prev => ({
                        ...prev,
                        id_colaborador,
                        fecha_inicio: colaborador.fecha_ingreso ? colaborador.fecha_ingreso.split('T')[0] : '',
                        sueldo_base: colaborador.sueldo_base || '',
                        cargo: colaborador.cargo || '',
                        jornada: colaborador.jornada || 'COMPLETA',
                        horas_semanales: colaborador.horas_semanales || 45,
                        lugar_trabajo: colaborador.direccion || ''
                    }));
                }
            } catch (error) {
                console.error('Error al cargar datos del colaborador:', error);
            }
        }
    };

    const resetForm = () => {
        setFormData({
            id_colaborador: '',
            tipo_contrato: 'INDEFINIDO',
            fecha_inicio: '',
            fecha_termino: '',
            sueldo_base: '',
            jornada: 'COMPLETA',
            horas_semanales: 45,
            cargo: '',
            lugar_trabajo: '',
            observaciones: ''
        });
    };

    return (
        <div className="contratos-container">
            <div className="contratos-header">
                <h1><FaFileContract /> Contratos Laborales</h1>
                <button className="btn-nuevo" onClick={() => { resetForm(); setShowModal(true); }}>
                    <FaPlus /> Nuevo Contrato
                </button>
            </div>

            {/* Filtros */}
            <div className="contratos-filters">
                <div className="filter-group">
                    <label>Estado</label>
                    <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                        <option value="">Todos</option>
                        <option value="VIGENTE">Vigentes</option>
                        <option value="TERMINADO">Terminados</option>
                    </select>
                </div>
                <div className="filter-group">
                    <label>Colaborador</label>
                    <select value={filtroColaborador} onChange={(e) => setFiltroColaborador(e.target.value)}>
                        <option value="">Todos</option>
                        {colaboradores.map(col => (
                            <option key={col.id} value={col.id}>
                                {col.nombres} {col.apellido_paterno}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Tabla de contratos */}
            {loading ? (
                <div className="loading">Cargando contratos...</div>
            ) : (
                <div className="contratos-table-container">
                    <table className="contratos-table">
                        <thead>
                            <tr>
                                <th>Colaborador</th>
                                <th>Tipo Contrato</th>
                                <th>Fecha Inicio</th>
                                <th>Fecha Término</th>
                                <th>Sueldo Pactado</th>
                                <th>Jornada</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {contratos.length === 0 ? (
                                <tr>
                                    <td colSpan="8" style={{ textAlign: 'center', padding: '30px' }}>
                                        No se encontraron contratos
                                    </td>
                                </tr>
                            ) : (
                                contratos.map((contrato) => (
                                    <tr key={contrato.id}>
                                        <td>{contrato.nombres} {contrato.apellido_paterno}</td>
                                        <td>{contrato.tipo_contrato}</td>
                                        <td>{new Date(contrato.fecha_inicio).toLocaleDateString('es-CL')}</td>
                                        <td>
                                            {contrato.fecha_termino
                                                ? new Date(contrato.fecha_termino).toLocaleDateString('es-CL')
                                                : 'Indefinido'
                                            }
                                        </td>
                                        <td>${parseInt(contrato.sueldo_base || 0).toLocaleString('es-CL')}</td>
                                        <td>{contrato.jornada} ({contrato.horas_semanales}h)</td>
                                        <td>
                                            <span className={`badge badge-${contrato.estado?.toLowerCase() || 'vigente'}`}>
                                                {contrato.estado || 'VIGENTE'}
                                            </span>
                                        </td>
                                        <td className="actions-cell">
                                            {contrato.url_contrato_firmado && (
                                                <button
                                                    className="btn-download"
                                                    onClick={() => window.open(contrato.url_contrato_firmado.startsWith('http') ? contrato.url_contrato_firmado : `${API_BASE_URL}/uploads/${contrato.url_contrato_firmado}`, '_blank')}
                                                    title="Ver PDF"
                                                >
                                                    <FaFileContract />
                                                </button>
                                            )}
                                            {contrato.url_contrato_firmado && (
                                                <button
                                                    className="btn-info"
                                                    onClick={() => handleReemplazarPdf(contrato)}
                                                    title="Reemplazar PDF"
                                                >
                                                    <FaSync />
                                                </button>
                                            )}
                                            {contrato.estado === 'VIGENTE' && (
                                                <button
                                                    className="btn-terminar"
                                                    onClick={() => handleTerminar(contrato.id)}
                                                    title="Terminar contrato"
                                                >
                                                    <FaBan />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal de formulario */}
            {showModal && (
                <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Nuevo Contrato</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Colaborador *</label>
                                    <select
                                        value={formData.id_colaborador}
                                        onChange={(e) => handleColaboradorChange(e.target.value)}
                                        required
                                    >
                                        <option value="">Seleccione...</option>
                                        {colaboradoresDisponibles.map(col => (
                                            <option key={col.id} value={col.id}>
                                                {col.nombres} {col.apellido_paterno}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Tipo de Contrato *</label>
                                    <select
                                        value={formData.tipo_contrato}
                                        onChange={(e) => setFormData({...formData, tipo_contrato: e.target.value})}
                                        required
                                    >
                                        <option value="INDEFINIDO">Indefinido</option>
                                        <option value="PLAZO_FIJO">Plazo Fijo</option>
                                        <option value="POR_OBRA">Por Obra o Faena</option>
                                        <option value="HONORARIOS">Honorarios</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Fecha Inicio *</label>
                                    <input
                                        type="date"
                                        value={formData.fecha_inicio}
                                        onChange={(e) => setFormData({...formData, fecha_inicio: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Fecha Término</label>
                                    <input
                                        type="date"
                                        value={formData.fecha_termino}
                                        onChange={(e) => setFormData({...formData, fecha_termino: e.target.value})}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Sueldo Base *</label>
                                    <input
                                        type="number"
                                        value={formData.sueldo_base}
                                        onChange={(e) => setFormData({...formData, sueldo_base: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Jornada Laboral *</label>
                                    <select
                                        value={formData.jornada}
                                        onChange={(e) => setFormData({...formData, jornada: e.target.value})}
                                        required
                                    >
                                        <option value="COMPLETA">Completa</option>
                                        <option value="PARCIAL">Parcial</option>
                                        <option value="TURNO">Por Turno</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Horas Semanales *</label>
                                    <input
                                        type="number"
                                        value={formData.horas_semanales}
                                        onChange={(e) => setFormData({...formData, horas_semanales: e.target.value})}
                                        required
                                        min="1"
                                        max="45"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Cargo *</label>
                                    <input
                                        type="text"
                                        value={formData.cargo}
                                        onChange={(e) => setFormData({...formData, cargo: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Domicilio del Trabajador</label>
                                    <input
                                        type="text"
                                        value={formData.lugar_trabajo}
                                        onChange={(e) => setFormData({...formData, lugar_trabajo: e.target.value})}
                                    />
                                </div>
                                <div className="form-group full-width">
                                    <label>Observaciones</label>
                                    <textarea
                                        value={formData.observaciones}
                                        onChange={(e) => setFormData({...formData, observaciones: e.target.value})}
                                        rows="3"
                                    />
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={() => { setShowModal(false); resetForm(); }}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-save">
                                    Generar Contrato
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

        </div>
    );
};

export default ContratosPage;
